import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useLocale } from "../../contexts/LocaleContext";

// public/index.html ships static description/og/twitter tags as a no-JS fallback
// (for crawlers and link-unfurlers that don't execute JavaScript). Once React
// mounts, every route sets its own tags via this component instead, so we strip
// the static ones here to avoid duplicate/conflicting <meta> tags in the live DOM.
// react-helmet-async only ever touches tags it created itself (marked data-rh),
// so this is the one place responsible for clearing the static fallback.
const STATIC_FALLBACK_SELECTOR = [
  'meta[name="description"]:not([data-rh])',
  'meta[name="keywords"]:not([data-rh])',
  'meta[property^="og:"]:not([data-rh])',
  'meta[name^="twitter:"]:not([data-rh])',
].join(", ");

const removeStaticFallbackTags = () => {
  document.head.querySelectorAll(STATIC_FALLBACK_SELECTOR).forEach((tag) => tag.remove());
};

export const SITE_URL = "https://www.catlogic.tech";
export const DEFAULT_OG_IMAGE = `${SITE_URL}/thumbnail.webp`;

type SeoProps = {
  title: string;
  description: string;
  path: string;
  keywords?: string;
  image?: string;
  type?: "website" | "article" | "profile";
  structuredData?: object | object[];
};

export default function Seo({ title, description, path, keywords, image = DEFAULT_OG_IMAGE, type = "website", structuredData }: SeoProps) {
  const { locale } = useLocale();
  const url = `${SITE_URL}${path}`;
  const ogLocale = locale === "ru" ? "ru_RU" : "en_US";
  const alternateLocale = locale === "ru" ? "en_US" : "ru_RU";
  const schemas = structuredData ? (Array.isArray(structuredData) ? structuredData : [structuredData]) : [];

  useEffect(() => {
    removeStaticFallbackTags();
  }, []);

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={url} />

      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="CatLogic" />
      <meta property="og:locale" content={ogLocale} />
      <meta property="og:locale:alternate" content={alternateLocale} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {schemas.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
}
